import React from 'react';
import { Card } from '../ui/Card';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { CashFlowData } from '../../types/financial';
import { formatCurrency } from '../../utils/pdfGenerator';
import './Financial.css';

interface CashFlowChartProps {
  data: CashFlowData[];
  loading?: boolean;
}

export const CashFlowChart: React.FC<CashFlowChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <Card title="Fluxo de Caixa">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Carregando dados...</p>
        </div>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card title="Fluxo de Caixa">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Nenhum dado disponível para o período selecionado.</p>
        </div>
      </Card>
    );
  }

  const formattedData = data.map(item => ({
    ...item,
    monthLabel: new Date(item.month + '-01').toLocaleDateString('pt-BR', {
      month: 'short',
      year: 'numeric',
    }),
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: 'var(--page-surface)',
            padding: '12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>
            {payload[0].payload.monthLabel}
          </p>
          {payload.map((entry: any, index: number) => (
            <p
              key={index}
              style={{
                margin: '4px 0',
                color: entry.color,
                fontSize: '14px',
              }}
            >
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Card title="Fluxo de Caixa Mensal">
      <div style={{ width: '100%', height: '400px', marginTop: '16px' }}>
        <ResponsiveContainer>
          <BarChart data={formattedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--page-border)" />
            <XAxis
              dataKey="monthLabel"
              stroke="var(--page-text-muted)"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="var(--page-text-muted)"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => {
                if (value >= 1000) {
                  return `R$ ${(value / 1000).toFixed(1)}k`;
                }
                return `R$ ${value}`;
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar
              dataKey="receitas"
              name="Receitas"
              fill="#10b981"
              radius={[8, 8, 0, 0]}
            />
            <Bar
              dataKey="despesas"
              name="Despesas"
              fill="#ef4444"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{ width: '100%', height: '300px', marginTop: '32px' }}>
        <ResponsiveContainer>
          <LineChart data={formattedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--page-border)" />
            <XAxis
              dataKey="monthLabel"
              stroke="var(--page-text-muted)"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="var(--page-text-muted)"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => {
                if (value >= 1000) {
                  return `R$ ${(value / 1000).toFixed(1)}k`;
                }
                return `R$ ${value}`;
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="saldo"
              name="Saldo Líquido"
              stroke="#0ea5e9"
              strokeWidth={3}
              dot={{ fill: '#0ea5e9', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};







